import { createContext, useContext, useState } from 'react';
/**
 * The app chrome: a thin icon rail, and the page frame that sits beside it.
 *
 * ── Why a rail ──────────────────────────────────────────────────────
 *
 * The web app had no navigation at all. Chats was the only screen you
 * could reach by clicking; /wallet and /earnings were URLs you had to
 * know and type, and once you were on one there was no way back except
 * the browser button. That is fine for a page you visit once and
 * survivable for a developer, and it is not fine for the screen where
 * people buy things.
 *
 * The rail is deliberately thin and icon-only. It is app chrome, not
 * content: the chat list beside it is already a navigation column, and
 * two full-width sidebars competing for the same job is how a layout
 * stops being readable.
 *
 * ── Why Wallet covers two routes ────────────────────────────────────
 *
 * Buying coins (/wallet) and withdrawing earnings (/earnings) are one
 * idea - money - reached from one rail item, with a sub-nav inside. They
 * were two unrelated top-level URLs, which is why somebody with a
 * balance and somebody with earnings each had to be told the other page
 * existed.
 */
import { NavLink, useNavigate } from 'react-router-dom';
import {
  ArrowLeft,
  Languages,
  LogOut,
  MessagesSquare,
  SquarePlay,
  Wallet as WalletIcon,
} from 'lucide-react';
import { Glyph, cn } from '@nsibi/ui-web';

import { OFFERED_LOCALES, nativeName } from '@nsibi/i18n';

import { currentLocale, setAppLocale } from '../lib/i18n';
import type { Session } from '../lib/session';
import { WEB_CHAT } from '../lib/features';
import { siteOrigin } from '../lib/siteOrigin';
import { useTranslation } from "react-i18next";

/** One destination in the rail. `match` is a prefix, so /wallet stays lit
 *  on /wallet/checkout/<id> and Wallet covers /earnings too. */
type RailItem = {
  to: string;
  label: string;
  icon: typeof MessagesSquare;
  match: string[];
};

/** Signing out, from the rail. The chat page used to be the only place it
 *  was offered; with the web's chat off it lives here (App provides it). */
export const SignOutContext = createContext<(() => void) | null>(null);

const ITEMS: RailItem[] = [
  ...(WEB_CHAT
    ? [{ to: '/chats', label: 'Chats', icon: MessagesSquare, match: ['/chats'] }]
    : []),
  // SquarePlay, matching the app's `play-box`: a rounded square with a
  // play triangle in it. The phone picked that glyph deliberately - the
  // feed is video, and the icon should say what is behind the tap - and
  // the two clients naming the same tab with different pictures is how a
  // product stops feeling like one product. (Sparkles was here first and
  // was wrong twice over: it reads as "AI", and it matches nothing.)
  { to: '/feed', label: 'Feed', icon: SquarePlay, match: ['/feed'] },
  { to: '/wallet', label: 'Wallet', icon: WalletIcon, match: ['/wallet', '/earnings'] },
];

export function NavRail({ pathname, session }: { pathname: string; session?: Session }) {
  const { t } = useTranslation();
  const signOut = useContext(SignOutContext);
  return (
    <nav
      aria-label={t("components.sections")}
      className="flex w-14 shrink-0 flex-col items-center gap-1 border-r border-border bg-muted/30 py-3 md:w-16"
    >
      {/* The mark is the way to the public site, in a tab of its own so
          the app keeps its place - every other link from here to
          nsibi.app opens the same way. */}
      <a
        href={siteOrigin()}
        target="_blank"
        rel="noopener noreferrer"
        title="nsibi.app"
        aria-label="nsibi.app"
        className="mb-2 grid size-9 place-items-center rounded-xl transition-colors hover:bg-muted"
      >
        <Glyph className="size-6" />
      </a>
      {ITEMS.map((item) => {
        const active = item.match.some(
          (p) => pathname === p || pathname.startsWith(`${p}/`),
        );
        const Icon = item.icon;
        return (
          <NavLink
            key={item.to}
            to={item.to}
            aria-label={item.label}
            aria-current={active ? 'page' : undefined}
            // The label is present for screen readers via aria-label and
            // shown on hover for everyone else. A rail this narrow cannot
            // carry text without becoming a sidebar, which is the thing it
            // is deliberately not.
            title={item.label}
            className={cn(
              'group relative grid size-11 place-items-center rounded-xl transition-colors',
              active
                ? t("components.bgPrimary10TextPrimary")
                : t("components.textMutedForegroundHoverBg"),
            )}
          >
            <Icon className="size-5" />
            {active ? (
              <span
                aria-hidden
                className="absolute left-0 top-1/2 h-5 w-0.5 -translate-y-1/2 rounded-r bg-primary"
              />
            ) : null}
          </NavLink>
        );
      })}
      {/* Pinned to the bottom of the rail. Language is a setting people
          look for once and then never again, so it must be FINDABLE
          without occupying a place in the primary navigation - and it
          has to be reachable by icon alone, because somebody who landed
          in a language they cannot read cannot use a text label to get
          out of it. */}
      <div className="mt-auto flex flex-col items-center gap-1">
        <LocalePicker />
        {signOut ? (
          <button
            type="button"
            onClick={signOut}
            aria-label={t("routes.signOut")}
            title={t("routes.signOut")}
            className="grid size-11 place-items-center rounded-xl text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
          >
            <LogOut className="size-5" />
          </button>
        ) : null}
        {/* Who is signed in, at the foot of the rail - it used to head
            every page, where the watch pages want their search. It opens
            their own profile (`/me`), where their country is set. */}
        {session ? (
          <NavLink
            to="/me"
            title={session.user.display_name || t("components.you")}
            aria-label={session.user.display_name || t("components.you")}
            className="mt-1 grid size-11 place-items-center rounded-xl transition-colors hover:bg-muted"
          >
            {session.user.avatar_url ? (
              <img
                src={session.user.avatar_url}
                alt={session.user.display_name || t("components.you")}
                className="size-8 rounded-full object-cover ring-1 ring-border"
              />
            ) : (
              <span className="grid size-8 place-items-center rounded-full bg-muted text-sm font-semibold">
                {(session.user.display_name || '?').slice(0, 1).toUpperCase()}
              </span>
            )}
          </NavLink>
        ) : null}
      </div>
    </nav>
  );
}

/**
 * The language switcher.
 *
 * A native <select> on purpose. It is one control, it is keyboard- and
 * screen-reader-correct for free, and on a phone it opens the platform's
 * own picker - which is the fastest way through a six-item list on a
 * touch screen and the one every user already knows.
 *
 * Each language is written in ITSELF: somebody hunting for Portuguese is
 * looking for "Português", not for the current language's word for it.
 */
function LocalePicker() {
  const { t } = useTranslation();
  const [code, setCode] = useState(() => currentLocale());
  return (
    <label className="relative grid size-11 place-items-center rounded-xl text-muted-foreground hover:bg-muted hover:text-foreground">
      <span className="sr-only">{t("tabs.language")}</span>
      <Languages className="pointer-events-none absolute size-5" aria-hidden />
      <select
        aria-label={t("tabs.language")}
        value={code}
        title={nativeName(code)}
        onChange={(e) => {
          setCode(e.target.value);
          void setAppLocale(e.target.value);
        }}
        className="size-11 cursor-pointer appearance-none bg-transparent text-transparent"
      >
        {OFFERED_LOCALES.map((l) => (
          <option key={l.code} value={l.code} className="text-foreground">
            {l.native}
          </option>
        ))}
      </select>
    </label>
  );
}

/**
 * Back, as an icon.
 *
 * Prefers real history so it behaves the way the browser button does, and
 * falls back to `to` when there is none - which is the common case here,
 * because the wallet is often opened directly from a link the phone
 * handed over, with nothing behind it to go back to.
 */
export function BackButton({ to, label = 'Back' }: { to: string; label?: string }) {
  const navigate = useNavigate();
  return (
    <button
      type="button"
      aria-label={label}
      title={label}
      onClick={() => {
        if (window.history.length > 1) navigate(-1);
        else navigate(to);
      }}
      className="grid size-9 shrink-0 place-items-center rounded-full text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
    >
      <ArrowLeft className="size-5" />
    </button>
  );
}

/**
 * The frame for a full-page section beside the rail.
 *
 * Replaces two byte-identical `Shell` functions that had been copied
 * between Wallet.tsx and Earnings.tsx and differed only in a subtitle -
 * which is exactly how the back button came to be missing from both.
 */
export function AppShell({
  session,
  pathname,
  title,
  back,
  subnav,
  children,
  aside,
  mode = 'scroll',
  wide = false,
  header,
}: {
  session: Session;
  pathname: string;
  title: string;
  /** Where back goes when there is no history to pop. */
  back: string;
  subnav?: React.ReactNode;
  children: React.ReactNode;
  /**
   * A right rail beside the section - the feed's discovery column. From
   * `lg` it sits beside the page and the frame widens to hold both;
   * below that there is no room for it next to a readable column, so it
   * is not drawn. It sticks to the top of THIS pane's scroll, which is
   * the only thing on the screen that scrolls.
   */
  aside?: React.ReactNode;
  /**
   * `scroll`: an ordinary page in a scrolling pane. `fixed`: exactly the
   * window, nothing scrolls but what is inside - the watch page, whose
   * deck is a player where one wheel notch is one video (the public
   * site's `WatchShell mode="fixed"`, for the same reason).
   */
  mode?: 'scroll' | 'fixed';
  /** A grid wants more width than a column of text: the feed's mosaic. */
  wide?: boolean;
  /** Replaces the back-and-title row: the watch pages put search there. */
  header?: React.ReactNode;
}) {
  // One measure for the header band and the page under it, so their
  // edges line up.
  const width = cn(
    'max-w-2xl',
    wide && 'max-w-5xl',
    aside && (wide ? 'lg:max-w-[72rem] xl:max-w-[84rem]' : 'lg:max-w-[62rem] xl:max-w-[68rem]'),
  );
  const top = header ? (
    <header className={mode === 'fixed' ? 'mb-3' : 'mb-6'}>{header}</header>
  ) : (
    <header className={cn('flex items-center gap-3', mode === 'fixed' ? 'mb-3' : 'mb-6')}>
      <BackButton to={back} />
      <h1 className="min-w-0 truncate text-lg font-semibold">{title}</h1>
    </header>
  );

  if (mode === 'fixed') {
    return (
      <div className="flex h-full min-h-0">
        <NavRail pathname={pathname} session={session} />
        <div className="flex min-w-0 flex-1 flex-col overflow-hidden">
          <div
            className={cn(
              'mx-auto flex h-full min-h-0 w-full flex-col px-5 py-4',
              aside ? 'max-w-[84rem]' : 'max-w-5xl',
            )}
          >
            {top}
            <div className="flex min-h-0 flex-1 gap-8">
              <div className="min-h-0 min-w-0 flex-1">{children}</div>
              {aside ? (
                // Its own scroller: the page around it cannot move.
                <div className="hidden w-72 shrink-0 overflow-y-auto [scrollbar-width:none] lg:block [&::-webkit-scrollbar]:hidden">
                  {aside}
                </div>
              ) : null}
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="flex h-full min-h-0">
      <NavRail pathname={pathname} session={session} />
      {/* `data-scroll-pane`: the one thing on the screen that scrolls, so
          a page can find it to remember and restore its place. */}
      <div data-scroll-pane className="min-w-0 flex-1 overflow-y-auto">
        {header ? (
          // A page's own header (the watch pages' tabs, search and
          // recharge) runs the page's whole width, over the side column
          // too, and STAYS as the page scrolls - the way back to another
          // tab or a search is never a scroll away.
          <header className="sticky top-0 z-30 border-b border-border bg-background/95 backdrop-blur">
            <div className={cn('mx-auto w-full px-5 py-3', width)}>{header}</div>
          </header>
        ) : null}
        <div
          className={cn(
            'mx-auto w-full px-5',
            header ? 'pb-8 pt-5' : 'py-8',
            width,
            aside && (wide ? 'lg:flex lg:gap-8 xl:gap-12' : 'lg:flex lg:gap-8 xl:gap-12'),
          )}
        >
          <div className="min-w-0 flex-1">
          {header ? null : top}
          {subnav}
          {children}
          </div>
          {aside ? (
            <div className="hidden w-64 shrink-0 lg:block xl:w-72">
              {/* Under the sticky header when there is one. */}
              <div className={cn('sticky', header ? 'top-24' : 'top-8')}>{aside}</div>
            </div>
          ) : null}
        </div>
      </div>
    </div>
  );
}

/** Buy coins / Withdraw earnings, the two halves of the money section. */
export function WalletSubNav({ pathname }: { pathname: string }) {
  const { t } = useTranslation();
  const tabs = [
    { to: '/wallet', label: t("wallet.recharge") },
    { to: '/earnings', label: t("routes.earnings") },
  ];
  return (
    <div className="mb-6 flex gap-1 rounded-xl bg-muted/50 p-1">
      {tabs.map((tab) => {
        const active = pathname === tab.to || pathname.startsWith(`${tab.to}/`);
        return (
          <NavLink
            key={tab.to}
            to={tab.to}
            className={cn(
              'flex-1 rounded-lg px-3 py-2 text-center text-sm font-medium transition-colors',
              active
                ? t("components.bgBackgroundTextForegroundShadow")
                : t("components.textMutedForegroundHoverText"),
            )}
          >
            {tab.label}
          </NavLink>
        );
      })}
    </div>
  );
}
